import React, { type ComponentProps } from 'react'

import { Button, ButtonGroup, SolidCircleCheckIcon, Tooltip, useBreakpoint } from 'slab'
import { type GrapeMoney } from 'types'
import { convertGrapeMoneyToNumber } from 'utils'

import { useOnboarding } from '../OnboardingContext'
import {
  type DentalPlan,
  type OnboardingEnrollment,
  type OnboardingInfo,
  type PlanCardBadgeCategory,
  type VisionPlan,
} from '../types'
import { PlanCard } from './PlanCard'
import { getRecommendationReasonLabels } from '../utils'

type MemberOnboardingPlanCardProps = {
  badgeCategory?: PlanCardBadgeCategory;
  displayedPlan: VisionPlan | DentalPlan;
  currentPlan: OnboardingEnrollment | null;
  onSelectPlan: (plan: VisionPlan | DentalPlan) => void;
  sidebar: React.ReactNode;
  bodyContent: React.ReactNode;
  currentType: 'vision' | 'dental';
}

export const getRemainingAllowance = (
  onboardingData: OnboardingInfo,
  premium: number
) => {
  const allowanceSubunits =
    onboardingData?.allowance?.allowance_amount?.subunits
  const allowanceAmount = Number(((allowanceSubunits || 0) / 100).toFixed(2))
  return allowanceSubunits !== undefined ? allowanceAmount - premium : null
}

export const getRemainingAllowanceWithEnrollments = (
  onboardingData: OnboardingInfo,
  premium: number,
  currentType: 'medical' | 'vision' | 'dental'
) => {
  const remainingAllowance = getRemainingAllowance(onboardingData, premium)
  if (!remainingAllowance) {
    return null
  }
  const otherEnrollments = onboardingData?.enrollments?.filter((enrollment) => {
    if (currentType === 'medical') {
      return enrollment.covers_dental || enrollment.covers_vision
    } else if (currentType === 'vision') {
      return enrollment.covers_medical || enrollment.covers_dental
    } else {
      // dental
      return enrollment.covers_medical || enrollment.covers_vision
    }
  })
  // If there are no other enrollments, just return the remaining allowance
  if (!otherEnrollments || otherEnrollments?.length === 0) {
    return remainingAllowance
  }
  const remainingAllowanceWithEnrollments = otherEnrollments.reduce(
    (acc, enrollment) => {
      const enrollmentPremium = enrollment.quoted_premium
        ? convertGrapeMoneyToNumber(enrollment.quoted_premium as GrapeMoney)
        : 0
      return acc - enrollmentPremium
    },
    remainingAllowance
  )
  return remainingAllowanceWithEnrollments
}

export const SelectPlanButton = ({
  isCurrentPlan,
  isSelected,
  handleSelectPlan,
  buttonStyle,
}: {
  isCurrentPlan?: OnboardingEnrollment | null;
  handleSelectPlan: () => void;
  isSelected?: boolean | null;
  buttonStyle?: ComponentProps<typeof Button>['variant'];
}) => {
  const buttonVariant = !isCurrentPlan ? 'primary' : 'secondary'
  const labelText = isSelected ? 'Current plan' : 'Select plan'

  return (
    <>
      {isSelected && (
        <div className='flex justify-center items-center gap-1 text-thatch-600'>
          <SolidCircleCheckIcon height={20} width={20} />
          <p className='body-bold'>Selected</p>
        </div>
      )}
      {!isSelected && (
        <Button
          variant={buttonStyle || buttonVariant}
          onClick={handleSelectPlan}
        >
          {labelText}
        </Button>
      )}
    </>
  )
}

const MemberOnboardingPlanCard = ({
  badgeCategory,
  displayedPlan,
  currentPlan,
  onSelectPlan,
  sidebar,
  bodyContent,
  currentType,
}: MemberOnboardingPlanCardProps): React.ReactNode => {
  const { onboardingData } = useOnboarding()
  const { breakpointIsAtLeast } = useBreakpoint()

  const isSelected =
    currentPlan &&
    currentPlan.carrier_name?.toLowerCase() ===
      displayedPlan.carrier.toLowerCase() &&
    currentPlan.plan_code?.toLowerCase() ===
      displayedPlan.plan_code.toLowerCase()

  const premium = Number(
    ((displayedPlan.premium.subunits || 0) / 100).toFixed(2)
  )
  const difference = onboardingData
    ? getRemainingAllowanceWithEnrollments(onboardingData, premium, currentType)
    : null

  const premiumComponent = !!difference && (
    <PlanCard.Premium
      align={breakpointIsAtLeast('xl') ? 'right' : 'left'}
      amount={premium}
      remainder={difference}
    />
  )

  const cardLabel = badgeCategory ? getRecommendationReasonLabels(badgeCategory).cardLabel : null
  const tooltipLabel = badgeCategory ? getRecommendationReasonLabels(badgeCategory).tooltipLabel : null

  return (
    <>
      {badgeCategory && (
        <div className='text-left'>
          <Tooltip content={tooltipLabel}>
            <div className='ml-2 px-4 py-2 rounded-t-lg inline-block bg-violet-50 text-violet-500 body-sm-bold'>
              {cardLabel}
            </div>
          </Tooltip>
        </div>
      )}
      <PlanCard
        layout={breakpointIsAtLeast('xs') ? 'horizontal' : 'vertical'}
        selected={!!isSelected || (badgeCategory === 'renewal' && !currentPlan)}
      >
        <PlanCard.GraySection>{sidebar}</PlanCard.GraySection>
        <PlanCard.Section>
          <div className='flex flex-col xl:flex-row gap-6 xl:gap-4 h-full justify-between'>
            <div className='flex flex-col gap-6'>
              {!breakpointIsAtLeast('xl') && premiumComponent}
              <PlanCard.Features>{bodyContent}</PlanCard.Features>
            </div>
            <div className='flex flex-col justify-between'>
              {breakpointIsAtLeast('xl') && premiumComponent}
              <ButtonGroup>
                <SelectPlanButton
                  isCurrentPlan={currentPlan}
                  isSelected={isSelected}
                  handleSelectPlan={() => onSelectPlan(displayedPlan)}
                />
              </ButtonGroup>
            </div>
          </div>
        </PlanCard.Section>
      </PlanCard>
    </>
  )
}

export default MemberOnboardingPlanCard
